import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Landmark, ExternalLink } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { formatNumber, formatCurrency, dec, num } from "@/lib/formatters";
import { tooltipStyle, gruppeInfo } from "./oversiktConstants";

const COLORS = ["#103766", "#3F8A86", "#E8A34A", "#D35E4E"];
const mill = (v) => (v == null ? "—" : `${formatNumber(Math.round(v / 1000))} mill. kr`);
const pct = (v) => (v == null ? "—" : `${dec(v, 1)} %`);

export const FondSection = ({ data: payload, kommuneName, alertYears }) => {
  const rows = payload?.rows?.filter((r) => r.sum_fond != null);
  if (!rows?.length) return null;
  const labels = payload.fond_labels || {};
  const fields = Object.keys(labels);
  const last = rows[rows.length - 1];
  const first = rows[0];
  const chart = rows.map((r) => ({
    year: r.year,
    ...Object.fromEntries(fields.map((f) => [labels[f], r[f] ?? null])),
    _pct: r.pct,
    _sum: r.sum_fond,
  }));
  const g = gruppeInfo(payload);
  const fylkeKey = g.key;
  const utanFylke = g.utan;
  const pctChart = payload.rows.map((r) => ({
    year: r.year,
    Kommunen: r.pct,
    ...(utanFylke ? {} : { [fylkeKey]: g.pct(r) }),
    Landssnitt: r.land_pct,
  }));
  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="fond-card">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Landmark className="w-5 h-5 text-[#103766]" />
          Fondsoversikt – alle fond i kommunen
        </CardTitle>
        <p className="text-sm text-[#4B647F]">
          Disposisjonsfondet er fritt, mens bundne driftsfond og investeringsfond er øyremerkte midlar som berre kan brukast til bestemte føremål.
        </p>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="fond-sum">
            <p className="text-xs text-[#8AA4BF]">Sum fond {last.year}</p>
            <p className="text-2xl font-light text-[#1F2A36]">{mill(last.sum_fond)}</p>
            <p className="text-[11px] text-[#4B647F]">{formatCurrency(last.sum_fond)}</p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="fond-pct">
            <p className="text-xs text-[#8AA4BF]">Andel av driftsinntektene</p>
            <p className="text-2xl font-light text-[#1F2A36]">{pct(last.pct)}</p>
            <p className="text-[11px] text-[#4B647F]">{first.year}: {pct(first.pct)}</p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="fond-snitt">
            <p className="text-xs text-[#8AA4BF]">{utanFylke ? "Landssnitt" : `${g.kort} og land`} {last.year}</p>
            <p className="text-2xl font-light text-[#3F8A86]">{pct(utanFylke ? last.land_pct : g.pct(last))}</p>
            <p className="text-[11px] text-[#4B647F]">{utanFylke ? "Oslo er både kommune og fylke" : `Landet: ${pct(last.land_pct)}`}</p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="fond-fritt">
            <p className="text-xs text-[#8AA4BF]">Del disposisjonsfond {last.year}</p>
            <p className="text-2xl font-light text-[#1F2A36]">
              {last.sum_fond ? `${Math.round((last.disposisjonsfond / last.sum_fond) * 100)} %` : "—"}
            </p>
            <p className="text-[11px] text-[#4B647F]">av alle fonda er frie midlar</p>
          </div>
        </div>
        <div className="h-[320px]" data-testid="fond-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chart} barCategoryGap="30%">
              {endringsLinjer(chart)}
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
              <YAxis stroke="#8AA4BF" fontSize={12} width={90} tickFormatter={(v) => `${formatNumber(Math.round(v / 1000))} mill.`} />
              <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={tooltipStyle} />
              <Legend itemSorter={null} wrapperStyle={{ fontSize: 11 }} />
              {(alertYears || []).map((y) => (
                <ReferenceLine key={y} x={y} stroke="#C0392B" strokeDasharray="4 3" label={{ value: "Kraftig fall", position: "top", fontSize: 10, fill: "#C0392B" }} />
              ))}
              {fields.map((f, i) => (
                <Bar key={f} dataKey={labels[f]} stackId="fond" fill={COLORS[i % COLORS.length]} radius={i === fields.length - 1 ? [4, 4, 0, 0] : 0} />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">Sum fond i prosent av driftsinntektene – mot {utanFylke ? "landssnittet" : g.storby ? "storby- og landssnitt" : "fylkes- og landssnitt"}</p>
          <div className="h-[240px]" data-testid="fond-pct-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={pctChart}>
                {endringsLinjer(pctChart)}
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                <XAxis dataKey="year" stroke="#8AA4BF" fontSize={11} />
                <YAxis stroke="#8AA4BF" fontSize={11} width={50} tickFormatter={(v) => `${num(v)} %`} />
                <Tooltip formatter={(v) => pct(v)} contentStyle={tooltipStyle} />
                <Legend itemSorter={null} wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="Kommunen" stroke="#103766" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                {!utanFylke && <Line type="monotone" dataKey={fylkeKey} stroke="#3F8A86" strokeWidth={2} dot={{ r: 2 }} connectNulls />}
                <Line type="monotone" dataKey="Landssnitt" stroke="#9CA3AF" strokeWidth={2} dot={{ r: 2 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="fond-table">
            <thead>
              <tr className="border-b border-[#E6E9F0] text-[#4B647F]">
                <th className="py-2 pr-3 text-left font-medium">Fond {last.year}</th>
                <th className="py-2 px-3 text-right font-medium">1000 kr</th>
                <th className="py-2 pl-3 text-right font-medium">Andel av fonda</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((f) => (
                <tr key={f} className="border-b border-[#F1F4F8] text-[#1F2A36]" data-testid={`fond-row-${f}`}>
                  <td className="py-2 pr-3">{labels[f]}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{last[f] == null ? "—" : formatNumber(last[f])}</td>
                  <td className="py-2 pl-3 text-right tabular-nums">
                    {last[f] == null || !last.sum_fond ? "—" : `${dec(((last[f] / last.sum_fond) * 100), 1)} %`}
                  </td>
                </tr>
              ))}
              <tr className="font-medium text-[#1F2A36]">
                <td className="py-2 pr-3">Sum fond</td>
                <td className="py-2 px-3 text-right tabular-nums">{formatNumber(last.sum_fond)}</td>
                <td className="py-2 pl-3 text-right tabular-nums">100 %</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[#8AA4BF]">
          Kjelde: SSB{" "}
          <a href="https://www.ssb.no/statbank/table/13545" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            13545 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          – balanserekneskap, kommunekasse (1000 kr): C.1.1 Disposisjonsfond, C.1.2 Bundne driftsfond, C.2.1 Ubunde investeringsfond og C.2.2 Bundne investeringsfond. Snittene er uvekta gjennomsnitt av kommunane ({last.land_count} i landet).
        </p>
      </CardContent>
    </Card>
  );
};

export default FondSection;
